import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Download, FileDown, Loader2, RefreshCw } from "lucide-react";
import type { ExportBatchSummary, ExportFilters, ReportOptions } from "@crewledger/shared/schemas";
import { EmptyState, InfoBanner, ListTableSkeleton, PageHeader, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchReportOptions } from "@/features/reports/api";
import { cn } from "@/lib/utils";
import { MICROCOPY } from "@/lib/microcopy";
import {
  downloadExportBatch,
  fetchExportBatches,
  queueAuditExport,
  queueEquipmentExport,
  queueEquipmentUsagePdf,
  queueExceptionsExport,
  queuePayPeriodSummaryPdf,
  queuePayrollExport,
  queueSiteCostExport,
  queueSiteCostPdf,
  queueWorkerDetailPdf,
} from "./api";
import { useExportPermissions } from "./use-export-permissions";

const PDF_EXPORT_ACTIONS = [
  { key: "pdf-pay-period", label: "Pay Period Summary PDF", permission: "payroll" as const },
  { key: "pdf-site-cost", label: "Site Cost PDF", permission: "read" as const },
  { key: "pdf-worker-detail", label: "Worker Detail PDF", permission: "read" as const },
  { key: "pdf-equipment", label: "Equipment Usage PDF", permission: "read" as const },
];

const CSV_EXPORT_ACTIONS = [
  { key: "payroll", label: "Payroll Summary CSV", permission: "payroll" as const },
  { key: "site-cost", label: "Site Cost CSV", permission: "read" as const },
  { key: "equipment", label: "Equipment Usage CSV", permission: "read" as const },
  { key: "exceptions", label: "Exceptions CSV", permission: "read" as const },
  { key: "audit", label: "Audit Log CSV", permission: "audit" as const },
];

function statusLabel(status: ExportBatchSummary["status"]): string {
  if (status === "COMPLETED") return "Ready";
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function statusClass(status: ExportBatchSummary["status"]): string {
  switch (status) {
    case "COMPLETED":
      return "bg-green-50 text-green-700 border-green-200";
    case "FAILED":
      return "bg-red-50 text-red-700 border-red-200";
    case "PROCESSING":
      return "bg-blue-50 text-blue-700 border-blue-200";
    default:
      return "bg-amber-50 text-amber-700 border-amber-200";
  }
}

export function ExportsPage() {
  const { toast } = useToast();
  const { canRead, canExportPayroll, canExportAudit } = useExportPermissions();
  const [batches, setBatches] = useState<ExportBatchSummary[]>([]);
  const [options, setOptions] = useState<ReportOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [queueing, setQueueing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [payPeriodId, setPayPeriodId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [crew, setCrew] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const [jobCode, setJobCode] = useState("");
  const [costCode, setCostCode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filters: ExportFilters = {
    payPeriodId: payPeriodId || undefined,
    siteId: siteId || undefined,
    workerId: workerId || undefined,
    crew: crew || undefined,
    equipmentId: equipmentId || undefined,
    jobCode: jobCode || undefined,
    costCode: costCode || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  const loadBatches = useCallback(() => {
    setLoading(true);
    fetchExportBatches({ pageSize: 50 })
      .then((res) => { setBatches(res.data); })
      .catch((err: unknown) =>
        { setError(err instanceof Error ? err.message : "Failed to load exports"); },
      )
      .finally(() => { setLoading(false); });
  }, []);

  useEffect(() => {
    if (!canRead) return;
    loadBatches();
    fetchReportOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions(null); });
  }, [canRead, loadBatches]);

  useEffect(() => {
    if (!canRead) return;
    const hasPending = batches.some((b) => b.status === "PENDING" || b.status === "PROCESSING");
    if (!hasPending) return;
    const timer = window.setInterval(loadBatches, 4000);
    return () => { window.clearInterval(timer); };
  }, [canRead, batches, loadBatches]);

  async function handleQueue(key: string) {
    setQueueing(key);
    setError(null);
    try {
      switch (key) {
        case "payroll":
          await queuePayrollExport(filters);
          break;
        case "site-cost":
          await queueSiteCostExport(filters);
          break;
        case "equipment":
          await queueEquipmentExport(filters);
          break;
        case "exceptions":
          await queueExceptionsExport(filters);
          break;
        case "audit":
          await queueAuditExport(filters);
          break;
        case "pdf-pay-period":
          await queuePayPeriodSummaryPdf(filters);
          break;
        case "pdf-site-cost":
          await queueSiteCostPdf(filters);
          break;
        case "pdf-worker-detail":
          await queueWorkerDetailPdf(filters);
          break;
        case "pdf-equipment":
          await queueEquipmentUsagePdf(filters);
          break;
      }
      loadBatches();
      toast({
        variant: "success",
        title: "Export queued",
        description: "Your file will appear below when ready. Large exports may take a minute.",
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to queue export");
    } finally {
      setQueueing(null);
    }
  }

  if (!canRead) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Exports"
        description="Generate CSV and PDF payroll files. Downloads are permission-checked and stored privately."
        actions={
          <Button variant="outline" onClick={loadBatches}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <InfoBanner icon={FileDown} variant="info">
        {MICROCOPY.exportPermission} {MICROCOPY.auditLogSaved}
      </InfoBanner>

      <div className="space-y-3 rounded-xl border bg-card-white p-4">
        <p className="text-sm font-medium text-navy-deep">Filter exports</p>
        <p className="text-xs text-muted-foreground">
          Narrow results by pay period, site, or date range. Payroll CSV requires a pay period.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Pay period</label>
            <select
              value={payPeriodId}
              onChange={(e) => { setPayPeriodId(e.target.value); }}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All pay periods</option>
              {options?.payPeriods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Site</label>
            <select
              value={siteId}
              onChange={(e) => { setSiteId(e.target.value); }}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All sites</option>
              {options?.sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Worker</label>
            <select
              value={workerId}
              onChange={(e) => { setWorkerId(e.target.value); }}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All workers</option>
              {options?.workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.employeeId} — {w.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Crew</label>
            <select
              value={crew}
              onChange={(e) => { setCrew(e.target.value); }}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All crews</option>
              {options?.crews.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Equipment</label>
            <select
              value={equipmentId}
              onChange={(e) => { setEquipmentId(e.target.value); }}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All equipment</option>
              {options?.equipment.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.equipmentId} — {e.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Job code</label>
            <select
              value={jobCode}
              onChange={(e) => { setJobCode(e.target.value); }}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All job codes</option>
              {options?.jobCodes.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Cost code</label>
            <select
              value={costCode}
              onChange={(e) => { setCostCode(e.target.value); }}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All cost codes</option>
              {options?.costCodes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Date range</label>
            <div className="flex gap-2">
              <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); }} className="h-9" />
              <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); }} className="h-9" />
            </div>
          </div>
        </div>

        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">PDF exports</p>
        <div className="flex flex-wrap gap-2">
          {PDF_EXPORT_ACTIONS.map((action) => {
            const allowed =
              action.permission === "payroll" ? canExportPayroll : true;
            if (!allowed) return null;
            return (
              <Button
                key={action.key}
                variant="outline"
                size="sm"
                disabled={queueing === action.key}
                onClick={() => handleQueue(action.key)}
              >
                {queueing === action.key ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-1.5 h-4 w-4" />
                )}
                {action.label}
              </Button>
            );
          })}
        </div>

        <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">CSV exports</p>
        <div className="flex flex-wrap gap-2">
          {CSV_EXPORT_ACTIONS.map((action) => {
            const allowed =
              action.permission === "payroll"
                ? canExportPayroll
                : action.permission === "audit"
                  ? canExportAudit
                  : true;
            if (!allowed) return null;
            return (
              <Button
                key={action.key}
                variant="outline"
                size="sm"
                disabled={queueing === action.key}
                onClick={() => handleQueue(action.key)}
              >
                {queueing === action.key ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-1.5 h-4 w-4" />
                )}
                {action.label}
              </Button>
            );
          })}
        </div>
      </div>

      {error ? (
        <InfoBanner icon={FileDown} variant="error" title="Export failed">
          {error}
        </InfoBanner>
      ) : null}

      {loading && batches.length === 0 ? (
        <ListTableSkeleton rows={6} cols={5} />
      ) : batches.length === 0 ? (
        <EmptyState
          icon={FileDown}
          title="No exports yet"
          description="Queue an export above. Files are generated in the background and stored in R2."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b bg-bg-light text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Export</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Rows</th>
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3">Ready</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.id} className="border-b last:border-0 hover:bg-bg-light/50">
                  <td className="px-4 py-3">
                    <p className="font-medium">{batch.exportKind ?? batch.type}</p>
                    <p className="text-xs text-muted-foreground">
                      {batch.type} · {batch.fileName ?? batch.id.slice(0, 8)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
                        statusClass(batch.status),
                      )}
                    >
                      {batch.status === "PENDING" || batch.status === "PROCESSING" ? (
                        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                      ) : null}
                      {statusLabel(batch.status)}
                    </span>
                    {batch.errorMessage ? (
                      <p className="mt-1 text-xs text-red-600">{batch.errorMessage}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">{batch.rowCount ?? "—"}</td>
                  <td className="px-4 py-3">
                    <p>{batch.generatedByName ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(batch.createdAt).toLocaleString()}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    {batch.generatedAt ? new Date(batch.generatedAt).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {batch.status === "COMPLETED" && batch.downloadPath ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          void downloadExportBatch(
                            batch.id,
                            batch.fileName ??
                              (batch.type === "PDF" ? "werkio-export.pdf" : "werkio-export.csv"),
                          ).catch((err: unknown) => {
                            toast({
                              variant: "error",
                              title: "Download failed",
                              description: err instanceof Error ? err.message : undefined,
                            });
                          });
                        }}
                      >
                        <Download className="mr-1 h-4 w-4" />
                        Download
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
